// @vendors
import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux';

// @components
import {
  Box,
  DataGrid,
  Paper,
  Tooltip,
  FileDownloadOutlinedIcon,
  IconButton,
} from '../../../components/shared/MaterialUI'

// @components
import CustomPagination from './CustomPagination';
import CustomToolbar from './CustomToolbar';

// @assets
import { DashboardStyles } from '../../../assets/css/dashboard-style';
import { componentsStyles } from '../../../assets/css/tableComponents-styles';
import CustomNoRowsOverlay from '../../../assets/CustomNoRowsOverlay';

// @context
import { FilePageContext } from '../FilePage';

// @helpers
import { convertDateToUTCTexas, downloadDynamicFile } from '../../../helpers/FunctionsUtils';
import { LISTROLES } from '../../../helpers/ListRoles';

// @hooks
import UseMediaQuerys from '../../../hooks/UseMediaQuerys';
import { theme } from '../../../assets/theme';

const FileTable = () => {
  const styles = useMemo(() => DashboardStyles(), []);
  const { rowsState } = useContext(FilePageContext);
  const gState = useSelector((state) => state);
  const user = gState.user;
  const {isXs, isSm} = UseMediaQuerys();

  function capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const downloadResult = (responseFile, name) => {
    const fileName = `results_${name}`
    downloadDynamicFile(responseFile, fileName, '.csv');
  }

  const disabledButton = (category) => {
    if(category === 'add_employer')return hasPermission([LISTROLES.superAdmin, LISTROLES.orgManager, LISTROLES.orgProcessor, LISTROLES.orgReporter]);
    if(category === 'update_employer')return hasPermission([LISTROLES.superAdmin, LISTROLES.orgManager, LISTROLES.orgProcessor, LISTROLES.orgReporter]);
    if(category === 'add_employees')return hasPermission([LISTROLES.superAdmin, LISTROLES.orgManager, LISTROLES.orgProcessor, LISTROLES.orgReporter, LISTROLES.employerManager, LISTROLES.employerReporter]);
    if(category === 'update_employees')return hasPermission([LISTROLES.superAdmin]);
    if(category === 'update_state_certification')return hasPermission([LISTROLES.superAdmin, LISTROLES.orgManager, LISTROLES.orgProcessor, LISTROLES.orgReporter]);
    if(category === 'nsi')return hasPermission([LISTROLES.superAdmin, LISTROLES.orgManager, LISTROLES.orgProcessor, LISTROLES.orgReporter, LISTROLES.employerManager, LISTROLES.employerReporter]);
    if(category === 'update_nsi')return hasPermission([LISTROLES.superAdmin, LISTROLES.orgManager, LISTROLES.orgProcessor, LISTROLES.orgReporter]);
    return hasPermission([LISTROLES.superAdmin, LISTROLES.orgManager, LISTROLES.orgProcessor, LISTROLES.orgReporter, LISTROLES.employerManager, LISTROLES.employerReporter]);
  }

  const hasPermission = (roles) => {
    if(roles.includes(user?.role))return false;
    return true;
  }

  const columns = [
    {
      field: "name",
      headerName: "Name",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.5,
      renderCell: (params) => (
        <Tooltip
            title={<p style={{ fontSize: "16px", margin: "0" }}>{params?.row?.name}</p>}
            arrow
          >
            <Box sx={{textAlign: 'center', paddingLeft: '25px'}}>
              {`${params?.row?.name?.substring(0, 15)} ...`}
            </Box>
        </Tooltip>
      ),
    },
    {
      field: "category",
      headerName: "Category",
      sortable: false,
      filterable: false,
      hide: isXs || isSm ? true : false,
      disableColumnMenu: true,
      flex: 0.5,
      renderCell: (params) => (
        <Box sx={{textAlign: 'center', paddingLeft: '25px'}}>
          {params?.row?.category?.replace(/_/g, ' ')}
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      sortable: false,
      filterable: false,
      hide: isXs || isSm ? true : false,
      disableColumnMenu: true,
      flex: 0.5,
      renderCell: (params) => (
        <Box sx={{textAlign: 'center', paddingLeft: '25px'}}>
          {convertDateToUTCTexas(params?.row?.createdAt)}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hide: isXs ? true : false,
      flex: 0.5,
      renderCell: (params) => (
        <Box sx={{textAlign: 'center', paddingLeft: '25px'}}>
          {capitalizeFirstLetter(params?.row?.status.replace('_', ' '))}
        </Box>
      ),
    },
    {
      field: "responseHasErrors",
      headerName: "Error status",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.5,
      renderCell: (params) => (
        <Box sx={{textAlign: 'center', paddingLeft: '25px'}}>
          {params?.row?.responseHasErrors ? 'Errors' : 'Ok'}
        </Box>
      ),
    },
    {
      field: "responseFile",
      headerName: "Result",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.5,
      renderCell: (params) => (
        <Box sx={{textAlign: 'center', paddingLeft: '25px'}}>
          {params?.row?.status === 'done' &&
            <Tooltip
              title={<p style={{ fontSize: "16px", margin: "0" }}>Download result</p>}
              arrow
            >
              <IconButton
                  sx={{
                    color: theme.palette.secondary.black,
                    "&:disabled": {
                      color: theme.palette.secondary.lightGray,
                    },
                    "&:hover": {
                      backgroundColor: theme.cards.iconBackground
                    },
                  }}
                  onClick={() => downloadResult(params?.row?.responseFile, params?.row?.name)}
                  disabled={disabledButton(params.row.category)}
              >
                  <FileDownloadOutlinedIcon/>
              </IconButton>
            </Tooltip>
          }
        </Box>
      ),
    },
  ];

  return (
    <Paper elevation={4} sx={styles.wrapperTable}>
      <DataGrid
        columns={columns}
        components={{
          Toolbar: CustomToolbar,
          NoRowsOverlay: CustomNoRowsOverlay,
          Pagination: CustomPagination
        }}
        componentsProps={componentsStyles}
        headerHeight={80}
        rowHeight={70}
        paginationMode="server"
        pagination
        filterMode="server"
        disableSelectionOnClick
        checkboxSelection={false}
        disableColumnSelector
        sx={{
          ...styles.tableGrid,
          minHeight: 500
        }}
        {...rowsState}
      />
    </Paper>
  )
}

export default FileTable